/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import moment from "moment";
import { strings } from "../../../../common/Strings/Strings";
import { generateOptions, getGeneralError, getMonday, getSunday } from "../../../../util/helperFunctions";
import { ReservationTypeResponse } from "../../../../models/reservationtype/ReservationTypeResponse";
import logger from "../../../../util/logger";
import LoaderInline from "../../../../components/LoaderInline";
import AlertBox from "../../../../components/AlertBox";
import { ShiftForConsultationPeriodsResponse } from "../../../../models/shift/ShiftForConsultationPeriodsResponse";
import { Option } from "../../../../models/Option";
import ReservationCreationApi from "../../../../api/ReservationCreationApi";
import Select from "../../../../components/ReactHookFormFields/General/Select/Select";
import Calendar from "./OwnerCalendar";
import RadioButtonGroup from "../../../../components/ReactHookFormFields/General/RadioButton/RadioButtonGroup";
import { ClinicProfileResponse } from "../../../../models/clinic/profile/ClinicProfileResponse";

interface Props {
  clinic?: ClinicProfileResponse;
  reservationType?: ReservationTypeResponse;
}

const SelectTime: React.FC<Props> = ({ clinic, reservationType: resType }) => {
  const maxUpcomingWeekRange = 80;
  const [date, setDate] = useState(new Date());

  const [shifts, setShifts] = useState<ShiftForConsultationPeriodsResponse[]>([]);
  const [collabs, setCollabs] = useState<Option[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const [displayedShifts, setDisplayedShifts] = useState<ShiftForConsultationPeriodsResponse[]>([]);

  const [error, setError] = useState<string | null>(null);

  const {
    control,
    getValues,
    register,
    formState: { errors },
  } = useFormContext();

  const updateCalendar = (localShifts?: ShiftForConsultationPeriodsResponse[]) => {
    const [vet, collabId] = getValues(["vet", "collabId"]);
    const ss = localShifts || shifts;
    setDisplayedShifts(ss.filter((s) => vet === "anyVet" || s.collaborator.userId === collabId));
  };

  useEffect(() => {
    const getConsultationPeriods = async (clinicId: string) => {
      const startDate = moment(getMonday(date)).set({ hour: 0, minute: 0, second: 0 }).toDate();

      const endDate = moment(getSunday(date)).set({ hour: 23, minute: 59, second: 59 }).toDate();

      try {
        const response = await ReservationCreationApi.getConsultationPeriods(clinicId, startDate, endDate, resType?.id);
        const resultShifts = response.data;
        setShifts(response.data);

        const collaborators = Object.values(
          Object.fromEntries(resultShifts.map((s) => s.collaborator).map((c) => [c.userId, c]))
        );

        const options: Option[] = generateOptions(collaborators, "fullName", "userId");
        setCollabs(options);
        setIsLoaded(true);

        updateCalendar(response.data);
      } catch (err) {
        setError(await getGeneralError(err));
        logger.error(err);
      }
    };

    if (clinic?.id) {
      void getConsultationPeriods(clinic.id);
    }
  }, [clinic?.id, date, resType?.id]);

  const setActualWeek = (newDate: Date) => {
    setDate(newDate);
  };

  return (
    <>
      <div>
        <h2 className="text-lg font-semibold leading-tight text-zinc-800 lg:text-xl dark:text-white">
          {strings.newAppointmentSubTitle3}
        </h2>
        <p>
          {clinic?.name} ({resType?.name} - {resType?.minutes} {strings.minutes})
        </p>
      </div>
      {isLoaded ? (
        <>
          <AlertBox message={error} />
          <div className="w-full md:w-1/2 space-y-6">
            {resType?.isVideoConsultationAllowed ? (
              <>
                <p>{strings.appointmentType}</p>
                <RadioButtonGroup
                  control={control}
                  name="videoConsultation"
                  options={[
                    { value: "inPerson", label: strings.inPersonExamination },
                    {
                      value: "video",
                      label: strings.onlineVideoConsultation,
                    },
                  ]}
                />
              </>
            ) : (
              <></>
            )}
            <p>{strings.appointmentCollaborator}</p>
            <RadioButtonGroup
              control={control}
              name="vet"
              onChange={() => {
                updateCalendar();
              }}
              options={[
                { value: "anyVet", label: strings.anyVet },
                {
                  value: "specificVet",
                  label: (
                    <Select
                      name="collabId"
                      label={strings.chooseADoctor}
                      disabledChooseOptionTitle={strings.chooseDoctor}
                      onChange={() => {
                        updateCalendar();
                      }}
                      options={collabs}
                      register={register}
                      error={errors.collabId}
                      required
                    />
                  ),
                },
              ]}
            />
          </div>
        </>
      ) : (
        <LoaderInline className="my-3" />
      )}
      <Controller
        name="reservationPeriod"
        control={control}
        render={({ field: { onChange } }) => (
          <>
            <div className="hidden lg:block">
              <Calendar
                defaultView="timeGridWeek"
                startDate={date}
                actualWeekMonday={getMonday(date)}
                actualWeekSunday={getSunday(date)}
                shifts={displayedShifts}
                minTime="07:00:00"
                maxTime="22:00:00"
                reservationLength={resType ? resType.minutes : 0}
                reservationName={resType ? resType.name : ""}
                maxUpcomingWeekRange={maxUpcomingWeekRange !== undefined ? maxUpcomingWeekRange : 2} // originally 2 was the default value
                changeWeek={(newDate: Date) => {
                  setActualWeek(newDate);
                }}
                onAppointmentChange={onChange}
              />
            </div>
            <div className="lg:hidden">
              <Calendar
                defaultView="timeGridDay"
                startDate={date}
                actualWeekMonday={getMonday(date)}
                actualWeekSunday={getSunday(date)}
                shifts={displayedShifts}
                minTime="07:00:00"
                maxTime="18:00:00"
                reservationLength={resType ? resType.minutes : 0}
                reservationName={resType ? resType.name : ""}
                maxUpcomingWeekRange={maxUpcomingWeekRange !== undefined ? maxUpcomingWeekRange : 2}
                changeWeek={(newDate: Date) => {
                  setActualWeek(newDate);
                }}
                onAppointmentChange={onChange}
              />
            </div>
          </>
        )}
        rules={{
          required: true,
        }}
      />
    </>
  );
};

export default SelectTime;
